import type { ReactNode } from "react";

import { Card, CardContent, CardHeader } from "@/components/ui/card";

export interface ReferenceSectionProps {
  /** Short label. Rendered uppercase — pass it in sentence case. */
  label: string;
  /** Optional trailing control, e.g. an upload button. */
  action?: ReactNode;
  children: ReactNode;
}

/**
 * A section of REFERENCE material: the activity log, the attachment list —
 * things you consult, not things you act on.
 *
 * Request-detail pages had one treatment for everything. Every section was a
 * card with a `text-base` title and the same padding, so a one-row audit log
 * claimed as much of the page as the form the page exists for, and a reader
 * had to actually read each heading to work out which was which.
 *
 * The first version of this component answered that by dropping the card
 * entirely. That was wrong: a label and a list sitting directly on the page
 * background reads as unfinished, not as subordinate — the container is what
 * makes a section look deliberate, and removing it removed the wrong thing.
 *
 * The card stays. What changes is the HEADER: a small uppercase muted label
 * instead of a `text-base` title, and tighter padding than a work card gets.
 * That is enough to rank it below the form beside it while still looking
 * finished, which is the whole job. Chrome says "this is a section"; type
 * scale and density say "this one is reference".
 */
export function ReferenceSection({
  label,
  action,
  children,
}: ReferenceSectionProps): JSX.Element {
  return (
    <Card>
      {/* p-4 not p-6, and no bottom padding: a reference header is a label,
          not a title block, so it does not need a work card's breathing room
          above its content. */}
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0 p-4 pb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </h3>
        {action}
      </CardHeader>
      <CardContent className="p-4 pt-0">{children}</CardContent>
    </Card>
  );
}
